<script setup lang="ts">
import { ref } from "vue";
import {
  AlertCircle, AlertTriangle, Info, X, Copy, Check, ChevronDown, ChevronUp,
} from "@lucide/vue";
import { useErrorStore } from "@/stores/errorStore";

const store = useErrorStore();
const expanded = ref<Record<number, boolean>>({});

const colorClass = (level: string) => {
  switch (level) {
    case "warning": return { border: "border-amber-300 dark:border-amber-800", bg: "bg-amber-50 dark:bg-amber-950/40", icon: "text-amber-600 dark:text-amber-400", title: "text-amber-900 dark:text-amber-200", message: "text-amber-800 dark:text-amber-300" };
    case "info": return { border: "border-blue-300 dark:border-blue-800", bg: "bg-blue-50 dark:bg-blue-950/40", icon: "text-blue-600 dark:text-blue-400", title: "text-blue-900 dark:text-blue-200", message: "text-blue-800 dark:text-blue-300" };
    default: return { border: "border-red-300 dark:border-red-800", bg: "bg-red-50 dark:bg-red-950/40", icon: "text-red-600 dark:text-red-400", title: "text-red-900 dark:text-red-200", message: "text-red-800 dark:text-red-300" };
  }
};

const copyState = ref<Record<number, boolean>>({});

const buildCopyText = (e: any) => {
  const lines: string[] = [];
  lines.push(`[${e.title}]`);
  lines.push(e.message);
  if (e.status) lines.push(`HTTP 状态: ${e.status}`);
  if (e.code) lines.push(`错误码: ${e.code}`);
  if (e.requestId) lines.push(`请求 ID: ${e.requestId}`);
  if (e.source) lines.push(`来源: ${e.source}`);
  if (e.detail) lines.push("");
  lines.push("详细:");
  lines.push(e.detail || "(无)");
  lines.push("");
  lines.push(`时间: ${new Date(e.createdAt).toLocaleString("zh-CN")}`);
  lines.push(`User-Agent: ${navigator.userAgent}`);
  return lines.join("\n");
};

const copy = async (e: any) => {
  try {
    await navigator.clipboard.writeText(buildCopyText(e));
    copyState.value[e.id] = true;
    setTimeout(() => { copyState.value[e.id] = false; }, 2000);
  } catch {
    // ignore
  }
};

const toggle = (id: number) => {
  expanded.value[id] = !expanded.value[id];
};
</script>

<template>
  <Teleport to="body">
    <div
      v-if="store.errors.length > 0"
      class="fixed top-4 left-1/2 -translate-x-1/2 z-[100] w-[min(720px,calc(100vw-32px))] space-y-2 pointer-events-none"
      role="alert"
      aria-live="polite"
    >
      <div
        v-for="e in store.errors"
        :key="e.id"
        class="pointer-events-auto rounded-lg shadow-2xl border-2"
        :class="[colorClass(e.level).border, colorClass(e.level).bg]"
      >
        <div class="p-3 flex items-start gap-3">
          <div class="flex-shrink-0 mt-0.5">
            <AlertCircle v-if="e.level === 'error'" class="h-5 w-5" :class="colorClass(e.level).icon" />
            <AlertTriangle v-else-if="e.level === 'warning'" class="h-5 w-5" :class="colorClass(e.level).icon" />
            <Info v-else class="h-5 w-5" :class="colorClass(e.level).icon" />
          </div>
          <div class="flex-1 min-w-0">
            <div class="flex items-center justify-between gap-2 flex-wrap">
              <h4 class="text-sm font-bold" :class="colorClass(e.level).title">{{ e.title }}</h4>
              <div class="flex items-center gap-1">
                <span v-if="e.status" class="text-[10px] font-mono text-muted-foreground px-1.5 py-0.5 rounded bg-background/60 border">{{ e.status }}</span>
                <span v-if="e.code" class="text-[10px] font-mono text-muted-foreground px-1.5 py-0.5 rounded bg-background/60 border">{{ e.code }}</span>
                <button
                  type="button"
                  class="text-xs px-2 py-0.5 rounded hover:bg-background/60 transition flex items-center gap-1"
                  :title="copyState[e.id] ? '已复制' : '复制错误详情'"
                  @click="copy(e)"
                >
                  <Check v-if="copyState[e.id]" class="h-3 w-3 text-green-600" />
                  <Copy v-else class="h-3 w-3" />
                  <span class="text-[10px]">{{ copyState[e.id] ? '已复制' : '复制详情' }}</span>
                </button>
                <button
                  type="button"
                  class="text-xs px-1.5 py-0.5 rounded hover:bg-background/60 transition flex items-center"
                  :title="expanded[e.id] ? '收起详情' : '展开详情'"
                  @click="toggle(e.id)"
                >
                  <ChevronUp v-if="expanded[e.id]" class="h-3.5 w-3.5" />
                  <ChevronDown v-else class="h-3.5 w-3.5" />
                </button>
                <button
                  v-if="e.dismissible"
                  type="button"
                  class="text-xs px-1.5 py-0.5 rounded hover:bg-background/60 transition"
                  title="关闭"
                  @click="store.dismiss(e.id)"
                >
                  <X class="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
            <p class="text-sm mt-0.5 font-mono break-words" :class="colorClass(e.level).message">{{ e.message }}</p>
            <p v-if="e.source" class="text-[10px] mt-0.5 text-muted-foreground font-mono truncate">📍 {{ e.source }}</p>
            <div v-if="expanded[e.id] && e.detail" class="mt-2 rounded bg-background/70 border border-current/20 p-2 text-[11px] font-mono whitespace-pre-wrap break-all max-h-64 overflow-auto">
              {{ e.detail }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
/* 错误横幅进场动画 */
.pointer-events-auto {
  animation: slide-down 0.18s ease-out;
}
@keyframes slide-down {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>